import { ViewDensity, ViewType } from "@common/types/views/views";
import {
    CheckDetailColumn,
    CheckDetailView,
} from "@common/types/views/checkDetailView";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import { formatMoneyColumnHeader } from "@common/helpers/number";
import { ViewConfig } from "@lib/views";
import {
    AMOUNT_COLUMN_SIZE,
    DATE_COLUMN_SIZE,
} from "@design-system/ViewTable/DefaultColumnSizes";
import {
    CheckDetailAccountCell,
    CheckDetailAmountCell,
    CheckDetailCheckNumberCell,
    CheckDetailClassCell,
    CheckDetailClearedDateCell,
    CheckDetailDateCell,
    CheckDetailDescriptionCell,
    CheckDetailEntityCell,
    CheckDetailExcludeFrom1099Cell,
    CheckDetailMemoCell,
    CheckDetailPayeeCell,
    CheckDetailReconciledCell,
    CheckDetailReferenceCell,
} from "./checkDetailCells";
import { CheckDetailViewRow } from "@common/types/reports/check-detail";

const DATE_MIN_SIZE = 240;
const DATE_MAX_SIZE = 400;
const CHECK_NUMBER_SIZE = 100;
const REFERENCE_MIN_SIZE = 140;
const PAYEE_MIN_SIZE = 180;
const DESCRIPTION_MIN_SIZE = 200;
const MEMO_MIN_SIZE = 200;
const ENTITY_COLUMN_SIZE = 100;
const CLASS_MIN_SIZE = 100;
const ACCOUNT_MIN_SIZE = 180;
const EXCLUDE_FROM_1099_SIZE = 140;
const RECONCILED_SIZE = 100;
const DEFAULT_MAX_SIZE = 400;

export interface BuildCheckDetailConfigOptions {
    multiEntity?: boolean;
    numberFormat?: NumberFormatValues;
    isClassesEnabled?: boolean;
}

export function buildCheckDetailConfig(
    opts: BuildCheckDetailConfigOptions,
): ViewConfig<CheckDetailViewRow, string, never> {
    return {
        columns: {
            [CheckDetailColumn.DATE]: {
                name: "Account / Date",
                header: "Date",
                accessorKey: "rowKind",
                cell: CheckDetailDateCell,
                minSize: DATE_MIN_SIZE,
                maxSize: DATE_MAX_SIZE,
                immutable: true,
            },
            [CheckDetailColumn.CHECK_NUMBER]: {
                name: "Reference no.",
                header: "Reference no.",
                accessorKey: "rowKind",
                cell: CheckDetailCheckNumberCell,
                minSize: CHECK_NUMBER_SIZE,
                maxSize: DEFAULT_MAX_SIZE,
            },
            [CheckDetailColumn.REFERENCE]: {
                name: "Reference",
                header: "Reference",
                accessorKey: "rowKind",
                cell: CheckDetailReferenceCell,
                minSize: REFERENCE_MIN_SIZE,
                maxSize: DEFAULT_MAX_SIZE,
            },
            [CheckDetailColumn.PAYEE]: {
                name: "Counterparty",
                header: "Counterparty",
                accessorKey: "rowKind",
                cell: CheckDetailPayeeCell,
                minSize: PAYEE_MIN_SIZE,
                maxSize: DEFAULT_MAX_SIZE,
            },
            [CheckDetailColumn.DESCRIPTION]: {
                name: "Description",
                header: "Description",
                accessorKey: "rowKind",
                cell: CheckDetailDescriptionCell,
                minSize: DESCRIPTION_MIN_SIZE,
                maxSize: DEFAULT_MAX_SIZE,
            },
            [CheckDetailColumn.MEMO]: {
                name: "Memo",
                header: "Memo",
                accessorKey: "rowKind",
                cell: CheckDetailMemoCell,
                minSize: MEMO_MIN_SIZE,
                maxSize: DEFAULT_MAX_SIZE,
            },
            [CheckDetailColumn.ENTITY]: {
                name: "Entities",
                header: "Entities",
                accessorKey: "rowKind",
                cell: CheckDetailEntityCell,
                minSize: ENTITY_COLUMN_SIZE,
                maxSize: ENTITY_COLUMN_SIZE,
                hiddenFromSettings: !opts.multiEntity,
            },
            ...(opts.isClassesEnabled
                ? {
                      [CheckDetailColumn.CLASS]: {
                          name: "Class",
                          header: "Class",
                          accessorKey: "rowKind",
                          cell: CheckDetailClassCell,
                          minSize: CLASS_MIN_SIZE,
                          maxSize: DEFAULT_MAX_SIZE,
                      },
                  }
                : {}),
            [CheckDetailColumn.ACCOUNT]: {
                name: "Account",
                header: "Account",
                accessorKey: "rowKind",
                cell: CheckDetailAccountCell,
                minSize: ACCOUNT_MIN_SIZE,
                maxSize: DEFAULT_MAX_SIZE,
            },
            [CheckDetailColumn.EXCLUDE_FROM_1099]: {
                name: "Exclude from 1099",
                header: "Exclude from 1099",
                accessorKey: "rowKind",
                cell: CheckDetailExcludeFrom1099Cell,
                minSize: EXCLUDE_FROM_1099_SIZE,
                maxSize: EXCLUDE_FROM_1099_SIZE,
            },
            [CheckDetailColumn.CLEARED_DATE]: {
                name: "Cleared date",
                header: "Cleared date",
                accessorKey: "rowKind",
                cell: CheckDetailClearedDateCell,
                ...DATE_COLUMN_SIZE,
            },
            [CheckDetailColumn.RECONCILED]: {
                name: "Reconciled",
                header: "Reconciled",
                accessorKey: "rowKind",
                cell: CheckDetailReconciledCell,
                minSize: RECONCILED_SIZE,
                maxSize: RECONCILED_SIZE,
            },
            [CheckDetailColumn.AMOUNT]: {
                name: "Amount",
                header: formatMoneyColumnHeader("Amount", opts.numberFormat),
                accessorKey: "rowKind",
                cell: CheckDetailAmountCell,
                align: "right",
                ...AMOUNT_COLUMN_SIZE,
                immutable: true,
            },
        },
        nesting: {
            enabled: true,
            expandColumn: CheckDetailColumn.DATE,
            childrenKey: "children",
        },
    };
}

const PIN_ORDER = { left: 0, center: 1, right: 2 } as const;

export function getCheckDetailVisibleColumns(
    view: CheckDetailView,
    config: ViewConfig<CheckDetailViewRow, string, never>,
): CheckDetailColumn[] {
    return view.columns
        .filter((column) => column.id in config.columns)
        .toSorted(
            (a, b) =>
                PIN_ORDER[a.options?.pin ?? "center"] -
                PIN_ORDER[b.options?.pin ?? "center"],
        )
        .map((column) => column.id);
}

export function buildCheckDetailView(): CheckDetailView {
    return {
        type: ViewType.CHECK_DETAIL,
        columns: [
            { id: CheckDetailColumn.DATE, options: { pin: "left" } },
            { id: CheckDetailColumn.CHECK_NUMBER },
            { id: CheckDetailColumn.REFERENCE },
            { id: CheckDetailColumn.PAYEE },
            { id: CheckDetailColumn.DESCRIPTION },
            { id: CheckDetailColumn.MEMO },
            { id: CheckDetailColumn.ACCOUNT },
            { id: CheckDetailColumn.RECONCILED },
            {
                id: CheckDetailColumn.AMOUNT,
                options: { pin: "right" },
            },
        ],
        filters: {},
        options: {
            selection: false,
            bulkActions: false,
            density: ViewDensity.LG,
        },
    };
}
